import { DEFAULT_CHUNKED_UPLOAD_CONCURRENCY } from './chunked-upload';

export type DocumentUploadPoolKind = 'md5' | 'upload';

export type DocumentUploadPoolSizeResolver = (
  kind: DocumentUploadPoolKind
) => number;

/** Fallback when `navigator.hardwareConcurrency` is unavailable. */
const FALLBACK_HARDWARE_CONCURRENCY = 4;

/** MD5 runs in Web Workers; scale with cores, cap to limit worker overhead. */
const MD5_POOL_MIN = 1;
const MD5_POOL_MAX = 8;

/** Each upload already parallelizes blocks; keep file-level concurrency low. */
const UPLOAD_POOL_MIN = 1;
const UPLOAD_POOL_MAX = 3;

const clampPoolSize = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, Math.floor(value)));

export const getHardwareConcurrency = (): number => {
  const cores = globalThis.navigator?.hardwareConcurrency;

  if (typeof cores === 'number' && cores > 0) return cores;

  return FALLBACK_HARDWARE_CONCURRENCY;
};

/**
 * Default pool sizes derived from hardware concurrency with hard caps.
 *
 * MD5: one worker per concurrent file hash (`cores`, capped).
 * Upload: account for per-file chunk concurrency (`floor(cores / chunkConcurrency)`).
 */
export const resolveDefaultDocumentUploadPoolSize = (
  kind: DocumentUploadPoolKind,
  hardwareConcurrency = getHardwareConcurrency()
): number => {
  if (kind === 'md5')
    return clampPoolSize(hardwareConcurrency, MD5_POOL_MIN, MD5_POOL_MAX);

  return clampPoolSize(
    Math.floor(hardwareConcurrency / DEFAULT_CHUNKED_UPLOAD_CONCURRENCY),
    UPLOAD_POOL_MIN,
    UPLOAD_POOL_MAX
  );
};

let documentUploadPoolSizeResolver: DocumentUploadPoolSizeResolver =
  resolveDefaultDocumentUploadPoolSize;

export const resolveDocumentUploadPoolSize = (
  kind: DocumentUploadPoolKind
): number => Math.max(1, Math.floor(documentUploadPoolSizeResolver(kind)));

/** Override for tests or future adaptive tuning (e.g. file size, connection type). */
export const setDocumentUploadPoolSizeResolver = (
  resolver: DocumentUploadPoolSizeResolver
) => {
  documentUploadPoolSizeResolver = resolver;
};

export const resetDocumentUploadPoolSizeResolver = () => {
  documentUploadPoolSizeResolver = resolveDefaultDocumentUploadPoolSize;
};
